import { ticketDead, ticketNextStep, ticketPosted, ticketWord } from '../ticketWords';

export interface JourneyStop {
  title: string;
  detail: string;
  done: boolean;
}

export type NextStepTarget =
  | 'TICKET' | 'CREATE_TICKET' | 'UNDERWRITING' | 'DRAWDOWN' | 'SETTLEMENT' | 'EXCEPTIONS' | 'SIGNALS' | 'NONE';

export interface FacilityJourney {
  stops: JourneyStop[];
  /** Index of the stop the facility is at; -1 when every stop is done. */
  current: number;
  next: { headline: string; reason: string; action: string | null; target: NextStepTarget };
}

export interface FacilityTicket {
  id: number;
  status: string;
  /** A ticket with no portfolio yet cannot be submitted. */
  hasPortfolio: boolean;
}

/** What the facility page already knows about a private credit facility. Nothing here is computed anew. */
export interface PrivateCreditFacilityState {
  /** undefined while the ticket is still being read. */
  ticket: FacilityTicket | null | undefined;
  fromAgreement: boolean;
  commitment: string;
  funded: number;
  fundedText: string;
  /** IC packet state as recorded: DRAFT, SUBMITTED, APPROVED, or null when none is prepared. */
  packetStatus: string | null;
  /** True when this tenant refuses a first drawdown without an approved IC packet. */
  firstDrawNeedsApprovedPacket: boolean;
  /** Readiness recorded on the saved packet, 0–100; null when there is no packet. */
  packetReadiness: number | null;
  /** Required documents the saved packet marks as not ready. */
  packetDocumentsNotReady: string[];
  attention: Attention;
}

/** What is waiting on a loan: exceptions someone has recorded, and signals nobody has queued yet. */
export interface Attention {
  exceptions: number;
  signals: number;
  /** What the signals are, e.g. "4 covenant alerts". */
  signalsDetail: string;
}

/** The funding control needs all three; the page names whichever is missing. */
const READINESS_NEEDED = 80;

const plural = (count: number, word: string): string => `${count} ${word}${count === 1 ? '' : 's'}`;

/** The step for whatever is waiting, exceptions first; null when nothing is. */
const attentionStep = (attention: Attention): FacilityJourney['next'] | null => {
  if (attention.exceptions > 0) {
    return {
      headline: attention.exceptions === 1 ? '1 exception is open' : `${attention.exceptions} exceptions are open`,
      reason: `They are in the recorded worklist.${attention.signals > 0 ? ` ${attention.signalsDetail || plural(attention.signals, 'signal')} ${attention.signals === 1 ? 'is' : 'are'} also waiting for review.` : ''}`,
      action: 'Review exceptions', target: 'EXCEPTIONS',
    };
  }
  if (attention.signals > 0) {
    return {
      headline: `${attention.signalsDetail || plural(attention.signals, 'signal')} ${attention.signals === 1 ? 'is' : 'are'} waiting for review`,
      reason: 'Nothing has been recorded as an exception yet. Raise one only where follow-up is needed.',
      action: 'Review the signals', target: 'SIGNALS',
    };
  }
  return null;
};

const PACKET_WORDS: Record<string, string> = {
  DRAFT: 'is a draft', SUBMITTED: 'is submitted and waiting for approval', REJECTED: 'was rejected',
};

/**
 * Where a private credit facility stands and the one thing to do next. A direct loan is acquired
 * by registering the commitment (a ticket that posts with no cash), then funded by drawdowns.
 */
export const privateCreditJourney = (state: PrivateCreditFacilityState): FacilityJourney => {
  const ticket = state.ticket && !ticketDead(state.ticket.status) ? state.ticket : null;
  const posted = Boolean(ticket && ticketPosted(ticket.status)) || state.funded > 0;
  const funded = state.funded > 0;
  const stops: JourneyStop[] = [
    { title: 'Agreement', detail: state.fromAgreement ? 'Imported from the credit agreement' : 'Entered in AxiomFlow', done: true },
    {
      title: 'Acquisition ticket',
      detail: state.ticket === undefined ? 'Checking…' : ticket ? `#${ticket.id} · ${ticketWord(ticket.status)}` : posted ? 'Posted' : 'Not created',
      done: posted,
    },
    { title: 'Commitment in IBOR', detail: posted ? `${state.commitment} committed` : 'When the ticket posts', done: posted },
    { title: 'Funded and servicing', detail: funded ? `${state.fundedText} funded` : 'Nothing funded yet', done: funded },
  ];
  const current = stops.findIndex(stop => !stop.done);

  if (state.ticket === undefined) {
    return { stops, current, next: { headline: 'Checking the acquisition ticket…', reason: '', action: null, target: 'NONE' } };
  }
  if (!posted && ticket) {
    return { stops, current, next: {
      headline: `Acquisition ticket #${ticket.id} is ${ticketWord(ticket.status).toLowerCase()}`,
      reason: `${ticketNextStep(ticket.status, ticket.hasPortfolio)} The commitment reaches IBOR when the ticket posts.`,
      action: 'Open the ticket in Blotter', target: 'TICKET',
    } };
  }
  if (!posted) {
    return { stops, current, next: {
      headline: 'This facility has no acquisition ticket',
      reason: 'It is reference data only until an acquisition ticket posts the commitment to IBOR.',
      action: 'Create the acquisition ticket', target: 'CREATE_TICKET',
    } };
  }
  if (!funded) {
    const packet = String(state.packetStatus || '').toUpperCase();
    // The control refuses a draw unless the packet is approved, at least 80% ready, and every
    // required document is ready. Name each part that is missing, not only the approval.
    const missing = [
      packet === 'APPROVED' ? null : `the IC packet ${PACKET_WORDS[packet] || 'has not been prepared'}`,
      state.packetReadiness != null && state.packetReadiness < READINESS_NEEDED
        ? `its readiness is ${state.packetReadiness}% (${READINESS_NEEDED}% needed)` : null,
      state.packetDocumentsNotReady.length
        ? `${state.packetDocumentsNotReady.join(', ')} ${state.packetDocumentsNotReady.length === 1 ? 'is' : 'are'} not ready` : null,
    ].filter(Boolean) as string[];
    if (state.firstDrawNeedsApprovedPacket && missing.length) {
      const reason = missing.join('; ');
      return { stops, current, next: {
        headline: packet === 'APPROVED'
          ? 'The IC packet is approved, but a drawdown would still be refused'
          : 'The commitment is in IBOR. The first drawdown needs an approved IC packet',
        reason: `${reason.charAt(0).toUpperCase()}${reason.slice(1)}. Funding needs the packet approved, at least ${READINESS_NEEDED}% ready, and every required document ready.`,
        action: 'Open underwriting', target: 'UNDERWRITING',
      } };
    }
    return { stops, current, next: {
      headline: 'The commitment is in IBOR. Nothing is funded yet',
      reason: 'Record the drawdown when the borrower’s notice arrives, or import the notice in Document Processing.',
      action: 'Record a drawdown', target: 'DRAWDOWN',
    } };
  }
  const waiting = attentionStep(state.attention);
  if (waiting) return { stops, current, next: waiting };
  return { stops, current, next: {
    headline: 'Nothing needs action',
    reason: 'The facility is funded and has no open items. Servicing actions are below.',
    action: null, target: 'NONE',
  } };
};

/** One trade on a leveraged loan that has posted but not yet settled. */
export interface PendingSettlement {
  tradeId: number;
  side: 'BUY' | 'SELL';
  parText: string;
  expectedSettleDate: string | null;
  /** Days past the expected settlement date on the book date; 0 or less when not yet due. */
  daysLate: number | null;
  settlementStatus: string | null;
  legalTransferStatus: string | null;
  exceptionReason: string | null;
}

/** What the facility card already reports for a leveraged loan. Nothing here is computed anew. */
export interface LeveragedLoanState {
  /** undefined while the facility card is still being read. */
  loaded: boolean;
  dealName: string | null;
  fromAgreement: boolean;
  /** The open purchase or sale ticket, if one has not posted yet. */
  ticket: FacilityTicket | null;
  pending: PendingSettlement[];
  holdingsExist: boolean;
  settledText: string;
  portfolioCount: number;
  attention: Attention;
}

/** When the trade settles, or how late it already is. */
const settleWords = (trade: PendingSettlement): string => {
  if (!trade.expectedSettleDate) return '';
  return trade.daysLate != null && trade.daysLate > 0
    ? `, which was due to settle ${trade.expectedSettleDate} and is ${plural(trade.daysLate, 'day')} late`
    : `, expected to settle ${trade.expectedSettleDate}`;
};

const TRANSFER_WORDS: Record<string, string> = {
  PENDING_CONSENT: 'the agent’s consent to the transfer is outstanding',
  EFFECTIVE: 'the legal transfer is effective',
};

/**
 * Where a leveraged loan stands and the one thing to do next. A syndicated loan is bought in the
 * secondary market: the deal is reference data, a ticket books the purchase, and the trade then
 * settles on LSTA terms before the fund is a lender of record.
 */
export const leveragedLoanJourney = (state: LeveragedLoanState): FacilityJourney => {
  const ticket = state.ticket && !ticketDead(state.ticket.status) && !ticketPosted(state.ticket.status) ? state.ticket : null;
  const failed = state.pending.find(trade => String(trade.settlementStatus || '').toUpperCase() === 'FAILED' || trade.exceptionReason);
  const bought = state.holdingsExist || state.pending.length > 0;
  const settled = state.holdingsExist && state.pending.length === 0;
  const stops: JourneyStop[] = [
    { title: 'Deal defined', detail: state.dealName ? `${state.dealName}${state.fromAgreement ? ' · from the credit agreement' : ''}` : 'Reference data', done: true },
    {
      title: 'Purchase ticket',
      detail: !state.loaded ? 'Checking…' : ticket ? `#${ticket.id} · ${ticketWord(ticket.status)}` : bought ? 'Posted' : 'None yet',
      done: bought && !ticket,
    },
    {
      title: 'Settlement',
      detail: !state.loaded ? 'Checking…' : state.pending.length ? `${state.pending.length} trade${state.pending.length === 1 ? '' : 's'} to settle`
        : state.holdingsExist ? 'Settled' : 'After the ticket posts',
      done: settled,
    },
    {
      title: 'Held and servicing',
      detail: state.holdingsExist ? `${state.settledText} settled in ${state.portfolioCount} portfolio${state.portfolioCount === 1 ? '' : 's'}` : 'No position yet',
      done: settled,
    },
  ];
  const current = stops.findIndex(stop => !stop.done);
  const build = (next: FacilityJourney['next']): FacilityJourney => ({ stops, current, next });

  if (!state.loaded) return build({ headline: 'Checking the loan’s tickets and holdings…', reason: '', action: null, target: 'NONE' });
  if (ticket) {
    return build({
      headline: `Ticket #${ticket.id} is ${ticketWord(ticket.status).toLowerCase()}`,
      reason: `${ticketNextStep(ticket.status, ticket.hasPortfolio)} The position appears in IBOR when the ticket posts; settlement follows.`,
      action: 'Open the ticket in Blotter', target: 'TICKET',
    });
  }
  if (failed) {
    return build({
      headline: `Settlement of trade #${failed.tradeId} needs attention`,
      reason: failed.exceptionReason || 'The settlement failed. Resolve it in the settlement queue.',
      action: 'Open the settlement queue', target: 'SETTLEMENT',
    });
  }
  if (state.pending.length) {
    const first = state.pending[0];
    const transfer = TRANSFER_WORDS[String(first.legalTransferStatus || '').toUpperCase()];
    return build({
      headline: state.pending.some(trade => (trade.daysLate || 0) > 0)
        ? (state.pending.length === 1 ? `Trade #${first.tradeId} is late to settle` : `${state.pending.length} trades are waiting to settle, some past due`)
        : (state.pending.length === 1 ? `Trade #${first.tradeId} is waiting to settle` : `${state.pending.length} trades are waiting to settle`),
      reason: `${first.side === 'SELL' ? 'Sale' : 'Purchase'} of ${first.parText}${settleWords(first)}${transfer ? `; ${transfer}` : ''}. The fund is a lender of record once it settles.`,
      action: 'Open the settlement queue', target: 'SETTLEMENT',
    });
  }
  if (!state.holdingsExist) {
    return build({
      headline: 'No portfolio holds this loan',
      reason: 'The deal is reference data. Book a purchase from a trade confirmation in Document Processing, or create the ticket here.',
      action: 'Create the acquisition ticket', target: 'CREATE_TICKET',
    });
  }
  const waiting = attentionStep(state.attention);
  if (waiting) return build(waiting);
  return build({
    headline: 'Nothing needs action',
    reason: 'The position is settled and has no open items.',
    action: null, target: 'NONE',
  });
};
